import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
}

// Function to generate chat completions using DeepSeek API via Supabase edge function
export async function generateDeepseekCompletion(
  messages: Array<{ role: string; content: string }>,
  model: string = "deepseek-coder",
  config?: AIConfig
): Promise<{ content: string; tokensUsed: number }> {
  // Add enhanced retry logic for network failures and service outages
  let retries = 5; // Increase max retries
  let lastError: any = null;
  let backoffDelay = 1000; // Start with 1 second delay, will increase exponentially

  const apiKey = localStorage.getItem('deepseek_api_key');
  
  while (retries > 0) {
    try {
      console.log(`Sending request to DeepSeek with model: ${model}`);
      
      // Log the request for debugging
      console.log('Sending full message structure to DeepSeek:', JSON.stringify(messages, null, 2));

      const { data, error } = await supabase.functions.invoke('deepseek-proxy', {
        body: {
          model: model,
          messages: messages,
          apiKey: apiKey, // Pass the API key if available
          temperature: config?.temperature,
          max_tokens: config?.max_tokens,
          top_p: config?.top_p,
        }
      });

      if (error) {
        console.error('Error invoking DeepSeek proxy function:', error);
        throw new Error(error.message || 'Failed to get response from DeepSeek');
      }

      if (!data || data.error) {
        const errorMessage = data?.error?.message || 'Failed to get response from DeepSeek';
        console.error('DeepSeek API response error:', data?.error);
        
        // Handle specific error cases based on DeepSeek API responses
        if (errorMessage.includes('API key')) {
          throw new Error("Invalid API key configuration for DeepSeek. Please contact the administrator or check your settings.");
        } else if (errorMessage.includes('quota')) {
          throw new Error("DeepSeek billing or quota issue. Please contact the administrator.");
        } else if (errorMessage.includes('model')) {
          throw new Error(`DeepSeek model error: ${errorMessage}. Please try a different model.`);
        }
        
        throw new Error(errorMessage);
      }
      
      console.log("Response received from DeepSeek");
      
      // Extract content from the response (adjust based on actual DeepSeek response structure)
      // Assuming the response structure is similar to OpenAI chat completions
      if (data.choices && data.choices.length > 0 && data.choices[0].message?.content) {
         return {
           content: data.choices[0].message.content,
           tokensUsed: data.usage?.total_tokens || 0,
         };
      } else {
         throw new Error("Unexpected response format from DeepSeek");
      }

    } catch (error: any) {
      lastError = error;
      
      // Only retry certain types of errors
      const isRetryableError = 
        error.message.includes('temporarily unavailable') || 
        error.message.includes('Network') || 
        error.message.includes('service') ||
        error.message.includes('Too many requests') ||
        error.message.includes('timeout') ||
        error.message.includes('429') || // Too many requests
        error.message.includes('503') || // Service unavailable
        error.name === 'AbortError' || // Timeout
        error.message.includes('fetch');
      
      if (isRetryableError && retries > 1) {
        console.warn(`Retrying DeepSeek API call, ${retries - 1} attempts remaining. Error: ${error.message}`);
        retries--;
        
        // Exponential backoff with jitter
        const jitter = Math.random() * 0.3 * backoffDelay;
        await new Promise(resolve => setTimeout(resolve, backoffDelay + jitter));
        backoffDelay *= 2; // Double the backoff delay for next retry
      } else {
        // Don't retry other types of errors or if we're on our last retry
        throw error;
      }
    }
  }
  
  // If we've exhausted retries
  throw lastError || new Error("Failed to get response from DeepSeek after multiple attempts. The service may be experiencing issues. Please try again later.");
}

// Function to check if the DeepSeek integration is working (Optional, requires a simple endpoint in proxy)
export async function checkDeepseekConnection(): Promise<boolean> {
  try {
    const apiKey = localStorage.getItem('deepseek_api_key');
    const { error } = await supabase.functions.invoke('deepseek-proxy', {
      body: {
        action: 'check_connection', // Add an action to tell the proxy to perform a check
        apiKey: apiKey
      }
    });
     if (error) {
        console.error('DeepSeek connection check failed via proxy:', error);
        return false;
    }
    return true;
  } catch (error) {
    console.error('Error checking DeepSeek connection:', error);
    return false;
  }
}